import { getQuestionnaireAnswers, MedplumClient } from "@medplum/core";
import {
  ClinicalImpression,
  CodeableConcept,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import { z } from "zod";

import { Systems } from "@/fhir/enums/systems";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";
import { localToUtcDateTimeString } from "@/utils/timeUtils";
import { qResponseString, toQuestionnaireResponse } from "@/utils/zod-questionnaires";

export enum ClientNote {
  Reminder = "clientReminderNote",
  Confidential = "clientConfidentialNote",
}

export enum ClientNoteStatus {
  InProgress = "in-progress",
  Completed = "completed",
  error = "entered-in-error",
}

export const ClientNoteForm = z.object({
  noteText: z.string(),
  noteStatus: z.enum(Object.values(ClientNoteStatus) as [string, ...string[]]),
});

export type ClientNoteTypeForm = z.infer<typeof ClientNoteForm>;
const CLIENT_REMINDER_NOTE_CODE = "forms/client-reminder/note";
const CLIENT_CONFIDENTIAL_NOTE_CODE = "forms/client-confidential/note";

export const clientNoteTypes = {
  clientReminderNote: {
    system: Systems.QH,
    code: CLIENT_REMINDER_NOTE_CODE,
    display: "Client Reminder Note",
  },
  clientConfidentialNote: {
    system: Systems.QH,
    code: CLIENT_CONFIDENTIAL_NOTE_CODE,
    display: "Client Confidential Note",
  },
} as const;

export type ClientNoteType = keyof typeof clientNoteTypes;

export const clientNoteStatusTypes = {
  clientReminderNote: {
    system: Systems.QH,
    code: "forms/client-reminder/status",
    display: "Client Reminder Status",
  },
  clientConfidentialNote: {
    system: Systems.QH,
    code: "forms/client-confidential/status",
    display: "Client Confidential Status",
  },
} as const;

export function generateClientNoteFormQuestionnaireResponse(
  form: ClientNoteTypeForm,
  type: ClientNoteType,
): QuestionnaireResponse {
  const questionnaireResponse: QuestionnaireResponse = toQuestionnaireResponse(form, {
    noteText: (form, fieldName) =>
      form.noteText
        ? qResponseString({
            fieldName,
            value: form.noteText,
            system: Systems.QH,
            code: clientNoteTypes[type].code,
            display: clientNoteTypes[type].display,
          })
        : null,
    noteStatus: (form, fieldName) =>
      form.noteStatus
        ? qResponseString({
            fieldName,
            value: form.noteStatus,
            system: Systems.QH,
            code: clientNoteStatusTypes[type].code,
            display: clientNoteStatusTypes[type].display,
          })
        : null,
  });
  return questionnaireResponse;
}

export async function saveClientNoteForm({
  medplum,
  form,
  clientId,
  practitionerReference,
  type,
}: {
  medplum: MedplumClient;
  form: ClientNoteTypeForm;
  clientId: string;
  practitionerReference: Reference<Practitioner>;
  type: ClientNoteType;
}): Promise<QuestionnaireResponse> {
  const CLIENT_NOTE_SYSTEM =
    type == ClientNote.Reminder
      ? `${Systems.QH}/${CLIENT_REMINDER_NOTE_CODE}`
      : `${Systems.QH}/${CLIENT_CONFIDENTIAL_NOTE_CODE}`;
  let questionnaireResponse = generateClientNoteFormQuestionnaireResponse(form, type);
  questionnaireResponse.questionnaire = CLIENT_NOTE_SYSTEM;
  questionnaireResponse.author = practitionerReference;
  questionnaireResponse.subject = {
    reference: `Patient/${clientId}`,
  };
  questionnaireResponse =
    await medplum.createResource<QuestionnaireResponse>(questionnaireResponse);
  return questionnaireResponse;
}

export async function saveClientClinicalImpression({
  medplum,
  questionnaireResponse,
}: {
  medplum: MedplumClient;
  questionnaireResponse: QuestionnaireResponse;
}) {
  const answers = getQuestionnaireAnswers(questionnaireResponse);
  const code: CodeableConcept = { coding: [answers["noteText-coding"].valueCoding!] };
  const status = answers["noteStatus-value"].valueString! as ClientNoteStatus;
  const summary: string = answers["noteText-value"].valueString!;
  const subject = questionnaireResponse.subject! as Reference<Patient>;
  // Store date in UTC for consistency using timeUtils
  const date = localToUtcDateTimeString(new Date().toISOString());
  const finding = [{ itemCodeableConcept: code }];
  const supportingInfo = [{ reference: `QuestionnaireResponse/${questionnaireResponse.id}` }];
  const assessor = questionnaireResponse.author as Reference<Practitioner>;

  const clinicalImpressionNote: ClinicalImpression = {
    resourceType: "ClinicalImpression",
    status,
    subject,
    code,
    summary,
    date,
    finding,
    supportingInfo,
    assessor,
  };

  const newClinicalImpressionNote =
    await medplum.createResource<ClinicalImpression>(clinicalImpressionNote);
  return newClinicalImpressionNote;
}

export async function fetchClientNoteClinicalImpressions({
  medplum,
  clientId,
  status = ClientNoteStatus.InProgress,
  type,
}: {
  medplum: MedplumClient;
  clientId: string;
  status?: string;
  _total?: number;
  type: ClientNoteType;
}): Promise<ClinicalImpression[]> {
  const findingCode =
    type == ClientNote.Reminder ? CLIENT_REMINDER_NOTE_CODE : CLIENT_CONFIDENTIAL_NOTE_CODE;
  return await medplum.searchResources("ClinicalImpression", {
    subject: `Patient/${clientId}`,
    "finding-code": findingCode,
    _sort: "-_lastUpdated",
    status,
  });
}

export async function fetchClientNoteQuestionnaireResponse({
  medplum,
  questionnaireResponseId,
}: {
  medplum: MedplumClient;
  questionnaireResponseId: string;
}): Promise<QuestionnaireResponse> {
  const questionnaireResponse = await medplum.readResource(
    "QuestionnaireResponse",
    questionnaireResponseId,
  );
  return questionnaireResponse;
}

export async function updateClientNoteStatus({
  medplum,
  clinicalImpression,
  status,
}: {
  medplum: MedplumClient;
  clinicalImpression: ClinicalImpression;
  status: ClientNoteStatus;
}): Promise<ClinicalImpression> {
  clinicalImpression.status = status;
  const updatedClinicalImpression =
    await medplum.updateResource<ClinicalImpression>(clinicalImpression);

  const { questionnaireResponseId } = getClinicalImpressionData(clinicalImpression);

  const questionnaireResponse = await fetchClientNoteQuestionnaireResponse({
    medplum,
    questionnaireResponseId,
  });

  questionnaireResponse.status = status;
  await medplum.updateResource<QuestionnaireResponse>(questionnaireResponse);

  return updatedClinicalImpression;
}

export async function updateClientNoteContent({
  medplum,
  clinicalImpression,
  form,
  practitionerReference,
}: {
  medplum: MedplumClient;
  clinicalImpression: ClinicalImpression;
  form: ClientNoteTypeForm;
  practitionerReference: Reference<Practitioner>;
}): Promise<ClinicalImpression> {
  const { questionnaireResponseId } = getClinicalImpressionData(clinicalImpression);

  // Update the questionnaire response with new content
  const questionnaireResponse = await fetchClientNoteQuestionnaireResponse({
    medplum,
    questionnaireResponseId,
  });

  // Merge updated answers and fields into the existing questionnaire response
  const mergedQuestionnaireResponse = {
    ...questionnaireResponse,
    ...generateClientNoteFormQuestionnaireResponse(form, ClientNote.Reminder),
    author: practitionerReference,
    subject: questionnaireResponse.subject,
    status: questionnaireResponse.status,
    authored: questionnaireResponse.authored,
    // Retain all other original properties (extensions, contained, etc.)
  };

  await medplum.updateResource<QuestionnaireResponse>(mergedQuestionnaireResponse);

  // Update the clinical impression
  clinicalImpression.summary = form.noteText;
  clinicalImpression.status = form.noteStatus as ClientNoteStatus;
  clinicalImpression.assessor = practitionerReference;

  const updatedClinicalImpression =
    await medplum.updateResource<ClinicalImpression>(clinicalImpression);

  return updatedClinicalImpression;
}
